import { useState, useCallback } from 'react';
import { useWizard } from '../../context/WizardContext';
import { FileUpload } from '../UI/FileUpload';
import { Button } from '../UI/Button';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { createInvoiceRequest, saveInvoiceRequest } from '../../services/storage';

export function AttachmentsStep() {
  const { state, updateFormData, prevStep, complete, setSubmitting } = useWizard();
  const [ticketFile, setTicketFile] = useState<File | null>(null);
  const [constanciaFile, setConstanciaFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleTicketSelect = useCallback((file: File | null) => {
    setTicketFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateFormData({
          attachments: {
            ...state.formData.attachments,
            ticket: {
              name: file.name,
              type: file.type,
              size: file.size,
              data: reader.result as string,
            },
          },
        });
      };
      reader.readAsDataURL(file);
    } else {
      updateFormData({
        attachments: {
          ...state.formData.attachments,
          ticket: undefined,
        },
      });
    }
  }, [state.formData.attachments, updateFormData]);

  const handleConstanciaSelect = useCallback((file: File | null) => {
    setConstanciaFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateFormData({
          attachments: {
            ...state.formData.attachments,
            constanciaFiscal: {
              name: file.name,
              type: file.type,
              size: file.size,
              data: reader.result as string,
            },
          },
        });
      };
      reader.readAsDataURL(file);
    } else {
      updateFormData({
        attachments: {
          ...state.formData.attachments,
          constanciaFiscal: undefined,
        },
      });
    }
  }, [state.formData.attachments, updateFormData]);

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);

    try {
      const invoiceRequest = createInvoiceRequest(state.formData);
      await saveInvoiceRequest(invoiceRequest);
      complete();
    } catch (error) {
      setSubmitError('Error al guardar la solicitud. Intenta nuevamente.');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const hasTicket = !!state.formData.attachments?.ticket || !!ticketFile;
  const hasPurchaseData = state.formData.purchaseInfo?.folio || 
                         state.formData.purchaseInfo?.purchaseDate || 
                         state.formData.purchaseInfo?.amount;

  const canSubmit = hasTicket || hasPurchaseData;

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Adjuntar documentos
        </h2>
        <p className="text-gray-600">
          Adjunta los documentos requeridos para procesar tu factura.
        </p>
      </div>

      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Ticket de compra *
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Adjunta tu ticket de compra en formato PDF, JPG o PNG (máx. 10MB).
          </p>
          <FileUpload
            accept="application/pdf,image/jpeg,image/png"
            maxSize={10 * 1024 * 1024}
            onFileSelect={handleTicketSelect}
            selectedFile={ticketFile}
            label=""
          />
          {!hasTicket && !hasPurchaseData && (
            <p className="mt-2 text-sm text-red-600">
              Debes adjuntar el ticket o haber proporcionado datos de compra en el paso anterior.
            </p>
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Constancia de Situación Fiscal (opcional)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Adjunta tu constancia de situación fiscal en formato PDF (máx. 10MB).
          </p>
          <FileUpload
            accept="application/pdf"
            maxSize={10 * 1024 * 1024}
            onFileSelect={handleConstanciaSelect}
            selectedFile={constanciaFile}
            label=""
          />
        </div>

        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{submitError}</p>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Requisitos:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Ticket de compra: PDF, JPG o PNG (máx. 10MB)</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Constancia fiscal: PDF (máx. 10MB)</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Los archivos se guardan de forma segura</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
        <Button
          onClick={prevStep}
          variant="ghost"
          icon={ArrowLeft}
        >
          Atrás
        </Button>
        <Button
          onClick={handleSubmit}
          loading={state.isSubmitting}
          disabled={!canSubmit || state.isSubmitting}
        >
          Enviar solicitud
        </Button>
      </div>
    </div>
  );
}